import { usePlatformLocale } from "../lib/platform-language";
import { translateUi } from "../lib/i18n.ts";
import { useEffect, useState, type FormEvent } from "react";
import { CheckCircle2, ChefHat, Loader2, LogIn, ShieldCheck } from "lucide-react";
import { apiFetch } from "../lib/api";

const monthlyFoodSchools = {
  "bgt-prishtine": { name: "BGT PRISHTINË 1", label: "BGT Prishtinë 1" },
  "bgt-prishtine-ii": { name: "BGT PRISHTINË 2", label: "BGT Prishtinë 2" },
  "bgt-prizren": { name: "BGT PRIZREN", label: "BGT Prizren" },
} as const;

type MonthlyFoodSchoolId = keyof typeof monthlyFoodSchools;
type MonthlyConfirmation = { isMonthly: boolean; confirmedAt: string };
type MonthlyConfirmationResponse = {
  ok: true;
  participant: { firstName: string; lastName: string; schoolEmail: string; role: "student" | "professor" };
  confirmation: MonthlyConfirmation | null;
  alreadyConfirmed?: boolean;
};

function monthlyFoodSchoolFromUrl(): MonthlyFoodSchoolId {
  if (typeof window === "undefined") return "bgt-prizren";
  const schoolId = new URLSearchParams(window.location.search).get("schoolId");
  return schoolId && schoolId in monthlyFoodSchools ? schoolId as MonthlyFoodSchoolId : "bgt-prizren";
}

function signInPath(schoolId: MonthlyFoodSchoolId) {
  const returnTo = `/ushqimi-mujor?schoolId=${encodeURIComponent(schoolId)}`;
  return `/?return_to=${encodeURIComponent(returnTo)}`;
}

function requestStatus(error: unknown) {
  return typeof error === "object" && error !== null && "status" in error && typeof error.status === "number"
    ? error.status
    : null;
}

export function PublicMonthlyFoodForm() {
  usePlatformLocale();
  const [schoolId] = useState(monthlyFoodSchoolFromUrl);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [participant, setParticipant] = useState<MonthlyConfirmationResponse["participant"] | null>(null);
  const [confirmation, setConfirmation] = useState<MonthlyConfirmation | null>(null);
  const [answer, setAnswer] = useState<boolean | null>(null);
  const [error, setError] = useState("");
  const [needsSignIn, setNeedsSignIn] = useState(false);
  const school = monthlyFoodSchools[schoolId];

  useEffect(() => {
    let active = true;
    setLoading(true);
    apiFetch<MonthlyConfirmationResponse>(`/api/food/monthly-confirmation?schoolId=${encodeURIComponent(schoolId)}`, {
      suppressUnauthorizedSessionEvent: true,
      retryTransientRead: true,
    })
      .then((result) => {
        if (!active) return;
        setParticipant(result.participant);
        setConfirmation(result.confirmation);
        setError("");
      })
      .catch((reason) => {
        if (!active) return;
        setNeedsSignIn(requestStatus(reason) === 401);
        setError(reason instanceof Error ? reason.message : translateUi("Formulari nuk mund të hapej. Provo përsëri.", "The form could not be opened. Try again."));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [schoolId]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (answer === null) {
      setError(translateUi("Zgjidh Po ose Jo.", "Select Yes or No."));
      return;
    }
    setSaving(true);
    setError("");
    try {
      const result = await apiFetch<MonthlyConfirmationResponse>("/api/food/monthly-confirmation", {
        method: "POST",
        body: JSON.stringify({ schoolId, isMonthly: answer }),
        suppressUnauthorizedSessionEvent: true,
      });
      setParticipant(result.participant);
      setConfirmation(result.confirmation);
    } catch (reason) {
      setNeedsSignIn(requestStatus(reason) === 401);
      setError(reason instanceof Error ? reason.message : translateUi("Konfirmimi nuk u ruajt. Provo përsëri.", "Confirmation could not be saved. Try again."));
    } finally {
      setSaving(false);
    }
  }

  return <main className="public-monthly-food-page">
    <section className="public-monthly-food-card" aria-busy={loading}>
      <header><img src="/assets/bgt-logo.png" alt="eduBGT" /><span>{school.name} {translateUi("· USHQIMI", "· FOOD")}</span></header>
      <div className="public-monthly-food-copy"><ChefHat size={25} /><h1>{translateUi("Konfirmimi i ushqimit mujor", "Monthly food confirmation")}</h1><p>{translateUi("Ky formular përdoret vetëm për konfirmimin e abonimit mujor në ", "This form is only for confirming a monthly subscription at ")}{school.label}{translateUi(". Nuk përdoret për porosi ushqimi.", ". It is not used for food orders.")}</p></div>
      {loading ? <div className="public-monthly-food-loading"><Loader2 className="spin" size={22} /> {translateUi("Po verifikohet llogaria...", "Verifying account...")}</div> : null}
      {!loading && confirmation ? <div className="public-monthly-food-success" role="status"><CheckCircle2 size={24} /><strong>{translateUi("Konfirmimi është ruajtur.", "Confirmation saved.")}</strong><span>{translateUi("Ke zgjedhur:", "You selected:")}<b>{confirmation.isMonthly ? translateUi("Po", "Yes") : translateUi("Jo", "No")}</b>{translateUi(". Ky formular mund të plotësohet vetëm një herë.", ". This form can only be submitted once.")}</span></div> : null}
      {!loading && !confirmation && participant ? <form onSubmit={(event) => void submit(event)}>
        <div className="public-monthly-food-identity"><ShieldCheck size={19} /><span><b>{participant.firstName} {participant.lastName}</b><small>{participant.schoolEmail} · {participant.role === "professor" ? translateUi("Profesor", "Professor") : translateUi("Nxënës", "Student")}</small></span></div>
        <fieldset className="public-monthly-food-question">
          <legend>{translateUi("A dëshiron ushqim mujor?", "Would you like a monthly meal subscription?")}</legend>
          <label className={answer === true ? "selected" : ""}><input type="radio" name="monthly" checked={answer === true} onChange={() => setAnswer(true)} /> <span>{translateUi("Po", "Yes")}</span></label>
          <label className={answer === false ? "selected" : ""}><input type="radio" name="monthly" checked={answer === false} onChange={() => setAnswer(false)} /> <span>{translateUi("Jo", "No")}</span></label>
        </fieldset>
        {error ? <p className="public-monthly-food-error" role="alert">{error}</p> : null}
        <button type="submit" disabled={saving}>{saving ? <><Loader2 className="spin" size={17} /> {translateUi("Po ruhet...", "Saving...")}</> : translateUi("Dërgo konfirmimin", "Submit confirmation")}</button>
      </form> : null}
      {!loading && !confirmation && !participant && error ? <div className="public-monthly-food-access" role="alert"><ShieldCheck size={23} /><strong>{needsSignIn ? translateUi("Kyçu për të vazhduar", "Sign in to continue") : translateUi("Nuk mund ta hapësh formularin", "You cannot open this form")}</strong><span>{error}</span>{needsSignIn ? <a href={signInPath(schoolId)}><LogIn size={17} /> {translateUi(" Kyçu me @bgt.school", " Sign in with @bgt.school")}</a> : null}</div> : null}
    </section>
  </main>;
}
